import { useTranslation } from 'next-i18next'
import { useEffect, useMemo, useRef, useState } from 'react'

import CloseButtonIcon from '~/components/Icons/svgs/CloseButtonIcon'
import SVGIcon from '~/components/Icons/svgs/SVGIcon'
import Loader from '~/components/Utility/Loader'
import { formatDateForCurriculumCertificate } from '~/shared/sanity/utils/curriculum.utils'
import useCurriculumGradeLabels from '~/curriculum/hooks/useCurriculumGradeLabels'

import { CurriculumCertificate } from '../types/curriculum.types'

interface Props {
  isOpen: boolean
  onClose: () => void
  name: string
  data: CurriculumCertificate
  isCertificateLoading?: boolean
  isPrintable?: boolean
}

const Certificate: React.FC<Props> = ({ isOpen, onClose, name, data, isPrintable, isCertificateLoading = false }) => {
  const { t } = useTranslation(['curriculum', 'common'])
  const printRef = useRef<HTMLDivElement>(null)
  const [scale, setScale] = useState(1)
  const gradeGroup = useMemo(() => [data], [data])
  const gradeLabelOf = useCurriculumGradeLabels(gradeGroup)
  const id = `Level-${data.gradeLevel}-${data.curriculumSubject}.pdf`
  const subjectFontSize =
    data.curriculumSubject.length > 25 ? 'text-8 leading-9 mx-14 w-9/10' : 'text-13 leading-16 w-full'

  useEffect(() => {
    if (isOpen) {
      // A4 dimensions in pixels
      const a4Width = 793
      const a4Height = 1122

      const screenWidth = window.innerWidth - 16
      const screenHeight = window.innerHeight - 128

      const widthScale = screenWidth / a4Width
      const heightScale = screenHeight / a4Height
      const finalScale = Math.min(widthScale, heightScale, 1)

      setScale(finalScale)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      className={`inset-0 flex bg-black  ${isPrintable ? 'opacity-0 pointer-events-none' : 'fixed bg-opacity-50'}`}
      onClick={onClose}
      style={{ zIndex: 99999 }}
    >
      <div className="grid items-end justify-center w-full mt-16 tracking-wide">
        {/* Modal container */}
        <div
          className="relative shadow-lg mt-8"
          onClick={(e) => e.stopPropagation()}
          style={{
            transform: `scale(${scale})`,
            transformOrigin: 'top',
          }}
        >
          {/* Close button */}
          <button
            className="absolute top-3 right-1 md:right-5 z-30 bg-purple-gmedium-dark/40 rounded-2 cursor-pointer"
            onClick={onClose}
          >
            <SVGIcon icon={CloseButtonIcon} pathClassName="text-white" size={40} />
          </button>
          {/* Modal content with fixed A4 dimensions */}
          <div id={id}>
            <div
              className={`relative p-6 gap-30 text-white w-[21cm] h-[29.7cm] overflow-auto print:w-[21cm] print:h-[29.7cm] flex flex-col justify-end bg-curriculum-certificate bg-no-repeat bg-contain ${
                isPrintable ? '' : 'rounded md:rounded-20'
              } `}
              ref={printRef}
            >
              <div className="absolute top-0 left-0 right-0 w-full">
                <div className="flex justify-center w-full">
                  <img
                    alt="imageAlt:icons.houseOfMath"
                    className="w-90"
                    loading="lazy"
                    src="https://images.houseofmath.com/curriculum/certificate/badge.png"
                    width="300"
                  />
                </div>
                <div className="absolute top-0 left-0 right-0 flex justify-center z-20">
                  <img
                    className="w-52 h-40"
                    loading="lazy"
                    src="https://images.houseofmath.com/curriculum/certificate/cropped-pentagon.png"
                  />
                </div>
                <div className="absolute top-12 left-0 right-0 flex justify-center z-20">
                  <img
                    className="w-30"
                    loading="lazy"
                    src="https://images.houseofmath.com/curriculum/certificate/hom-white-logo.png"
                  />
                </div>
              </div>
              <div className="grid justify-center text-center mb-10">
                <div className="w-full">
                  <h1 className="text-shadow-md text-center w-86 font-medium mx-auto text-5">
                    {t('mathematicsMasteryCertificate')}
                  </h1>

                  <h3 className={`mx-auto font-extrabold tracking-wide text-shadow-md text-orange ${subjectFontSize}`}>
                    {data.curriculumSubject}
                  </h3>
                  <h3 className="text-5 font-extrabold text-shadow-md">{gradeLabelOf(data)}</h3>
                </div>
                <div className="relative mb-18 mt-10">
                  <div className="flex justify-center w-full">
                    <img
                      className="w-96"
                      loading="lazy"
                      src="https://images.houseofmath.com/curriculum/certificate/leaf.png"
                    />
                  </div>
                  <div className="absolute inset-0 flex justify-center items-center">
                    <img
                      alt="imageAlt:icons.houseOfMath"
                      className="w-72 z-20"
                      height="300"
                      loading="lazy"
                      sizes="100vw"
                      src={`https://images.houseofmath.com/curriculum/certificate/mastery-certificate-${data.topicId}_v4.png`}
                      width="300"
                    />
                  </div>
                </div>
                <div className="">
                  <h4 className="text-5.5 font-medium text-green-emerald leading-none">{t('awardedTo')}</h4>
                  <h3 className="text-white text-8 tracking-wider font-extrabold italic">{name}</h3>
                </div>
              </div>

              <div className="mb-6 w-10/12 flex mx-auto gap-3">
                <div className="flex content-center justify-center items-center space-x-3 pt-1 w-1/2">
                  <Loader color="white" isLoading={isCertificateLoading} size="huge">
                    <div className="relative">
                      <img
                        className="w-36 z-20"
                        loading="lazy"
                        src="https://images.houseofmath.com/curriculum/certificate/blurred-pentagon.png"
                      />
                      <div className="absolute inset-0 flex flex-col items-center justify-center z-20">
                        <h3 className="text-10 font-extrabold text-shadow-md text-yellow-light leading-none tracking-wider">
                          {data.correctness}%
                        </h3>
                        <div className="text-green-emerald tracking-wide font-bold text-shadow-md leading-none">
                          {t('correctness')}
                        </div>
                      </div>
                    </div>
                  </Loader>
                </div>
                <div className="flex flex-col items-center justify-center text-start w-1/2 gap-4">
                  <div>
                    <div className="text-white w-36 border-b-2 border-dashed border-purple-light-top text-md italic">
                      House of Math
                    </div>
                    <div className="text-yellow-light font-extrabold">{t('signed')}</div>
                  </div>
                  <div>
                    <div className="text-white w-36 border-b-2 border-dashed border-purple-light-top text-md italic">
                      {formatDateForCurriculumCertificate(data.date)}
                    </div>
                    <div className="text-yellow-light font-extrabold">{t('date')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Certificate
